import { SelectHTMLAttributes, ChangeEvent, ReactElement } from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCompanySpaces } from '../queries/useCompanyQueries'
import { adjustColor } from '../theme/ajustColor'
import { useUI } from '../hooks/useUI'

type ListItem =
  | string
  | {
      label: string
      disabled?: boolean
      available?: boolean
    }

interface CustomSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label?: string
  value?: string
  onChange?: (event: SelectChangeEvent) => void
  listItems: ListItem[]
  required?: boolean
  error?: boolean
  helperText?: string
  validation?: { validate: () => boolean; msg: string }[]
  className?: string
  disabledEmpty?: boolean
}

export const CustomSelect = ({
  label,
  value = '',
  listItems,
  required,
  validation,
  error,
  helperText,
  className,
  onChange,
  disabledEmpty,
  ...rest
}: CustomSelectProps): ReactElement => {
  const { t } = useTranslation()
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!onChange) return

    const selectEvent: SelectChangeEvent = {
      target: {
        value: event.target.value,
        name: event.target.name || ''
      }
    } as SelectChangeEvent

    onChange(selectEvent)
  }

  return (
    <div>
      {label && (
        <label style={{ color: colors?.text }} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={handleChange}
          required={required}
          style={{
            backgroundColor: colors?.background,
            borderColor: colors.dark,
            color: colors?.text
          }}
          className={`
            w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none
            ${error ? 'border-red-500' : ''}
            ${className || ''}
          `}
          {...rest}
        >
          {disabledEmpty && (
            <option
              value=""
              disabled
              style={{ backgroundColor: colors?.background, color: colors?.text }}
            >
              {t('select')}
            </option>
          )}
          {listItems.map((item) => {
            const itemObj = typeof item === 'string' ? { label: item } : item
            return (
              <option
                key={itemObj.label}
                value={itemObj.label}
                disabled={itemObj.disabled}
                style={{
                  backgroundColor: colors?.background,
                  color: colors?.text
                }}
              >
                {itemObj.label}
              </option>
            )
          })}
        </select>
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && helperText && <p className="mt-1 text-sm text-red-600">{helperText}</p>}
    </div>
  )
}
